import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  RefreshCcw, Download, Check, Sparkles, CheckSquare, Square, Trash2,
  AlertTriangle, Wrench, ShieldCheck, Zap, Pause, Play, HardDrive,
} from "lucide-react";

const KINDS = [
  { key: "movies", label: "Movies" },
  { key: "tv", label: "TV Shows" },
  { key: "music", label: "Music" },
];

// qBittorrent 5.x state strings (older versions use pausedDL/pausedUP instead of
// stoppedDL/stoppedUP for the same thing — handled both since the running version can vary).
const TORRENT_STATE_LABELS = {
  downloading: "Downloading", forcedDL: "Downloading (forced)", metaDL: "Fetching metadata",
  stalledDL: "Stalled", queuedDL: "Queued", allocating: "Allocating",
  pausedDL: "Paused", stoppedDL: "Paused",
  uploading: "Seeding", forcedUP: "Seeding (forced)", stalledUP: "Seeding (idle)",
  queuedUP: "Queued to seed", pausedUP: "Completed", stoppedUP: "Completed",
  checkingDL: "Checking", checkingUP: "Checking", checkingResumeData: "Checking",
  moving: "Moving", error: "Error", missingFiles: "Missing files", unknown: "Unknown",
};

const formatBytes = (n) => {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatEta = (s) => {
  if (!s || s <= 0 || s >= 8640000) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// missing_files is intentionally excluded — the per-kind "Missing from disk" section above
// already covers it (via /scan/missing/{kind}), so showing it again here would just be the
// same findings twice under a different label.
const DEEP_CATEGORIES = [
  { key: "stuck_imports", label: "Stuck Radarr/Sonarr Imports", desc: "Fully downloaded but permanently rejected by Radarr/Sonarr's sample check, so it never auto-imports. Intact files are force-imported; unreadable ones are deleted and re-searched.", action: "Force import / re-search" },
  { key: "orphaned_episodes", label: "Orphaned Episodes", desc: "Episode references a show that no longer exists.", action: "Delete episode" },
  { key: "orphaned_hls", label: "Orphaned HLS Output", desc: "Transcoded HLS folders left behind by deleted movies/episodes.", action: "Delete folder" },
  { key: "stuck_jobs", label: "Stuck Transcode Jobs", desc: "Jobs stuck \"running\" for 2+ hours, usually from a backend restart mid-job.", action: "Mark failed" },
  { key: "duplicate_paths", label: "Duplicate Imports", desc: "The same file imported into the library more than once.", action: "Delete duplicate" },
  { key: "duplicate_content", label: "Duplicate Content", desc: "The same movie/episode/song imported from two different files — e.g. an album folder saved under both a truncated and a full name. Fixing this deletes the redundant file from the NAS, not just the database record.", action: "Delete duplicate file", destructive: true },
  { key: "orphaned_subtitles", label: "Orphaned Subtitles", desc: "Subtitle references a movie/episode that no longer exists.", action: "Delete subtitle" },
  { key: "dangling_refs", label: "Dangling Watchlist/Progress", desc: "Watchlist or progress rows pointing at a deleted movie.", action: "Delete row" },
];

// Every category the deep scan reports, for the top-of-page findings rundown (includes the two the
// deep-scan list omits because they're covered elsewhere: missing files and empty folders).
const RUNDOWN_CATEGORIES = [
  { key: "missing_files", label: "Missing files (in DB, not on disk)" },
  { key: "empty_folders", label: "Empty folders on the NAS" },
  { key: "duplicate_content", label: "Duplicate content (keep best, delete rest)", destructive: true },
  { key: "duplicate_paths", label: "Duplicate imports (same file twice)" },
  { key: "orphaned_episodes", label: "Orphaned episodes" },
  { key: "orphaned_hls", label: "Orphaned HLS output" },
  { key: "orphaned_subtitles", label: "Orphaned subtitles" },
  { key: "stuck_jobs", label: "Stuck transcode jobs" },
  { key: "stuck_imports", label: "Stuck Radarr/Sonarr imports" },
  { key: "dangling_refs", label: "Dangling watchlist/progress" },
];

export default function Library() {
  // ---- Downloads (qBittorrent — live-polling list + Pause All / Resume All) ----
  const [torrents, setTorrents] = useState(null); // null = not loaded yet / not configured
  const [torrentsError, setTorrentsError] = useState("");
  const [pausingAll, setPausingAll] = useState(false);
  const [resumingAll, setResumingAll] = useState(false);
  const downloadsPoll = useRef(null);

  const loadTorrents = async () => {
    try {
      const { data } = await api.get("/qbit/torrents");
      setTorrents(data);
      setTorrentsError("");
    } catch (err) {
      setTorrentsError(err.response?.data?.detail || "qBittorrent unreachable");
    }
  };

  useEffect(() => {
    loadTorrents();
    downloadsPoll.current = setInterval(loadTorrents, 3000);
    return () => clearInterval(downloadsPoll.current);
  }, []);

  useEffect(() => { loadCleanupCandidates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pauseAllDownloads = async () => {
    setPausingAll(true);
    try {
      await api.post("/qbit/pause-all");
      toast.success("Paused all downloads");
      loadTorrents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not pause downloads");
    } finally {
      setPausingAll(false);
    }
  };

  const resumeAllDownloads = async () => {
    setResumingAll(true);
    try {
      await api.post("/qbit/resume-all");
      toast.success("Resumed all downloads");
      loadTorrents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resume downloads");
    } finally {
      setResumingAll(false);
    }
  };

  // ---- Cleanup: completed downloads already imported into the library ----
  const [cleanupCandidates, setCleanupCandidates] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [selectedCleanup, setSelectedCleanup] = useState(new Set());
  const [cleaningUp, setCleaningUp] = useState(false);

  const loadCleanupCandidates = async () => {
    setCleanupLoading(true);
    try {
      const { data } = await api.get("/qbit/cleanup-candidates");
      setCleanupCandidates(data);
      setSelectedCleanup(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not check for cleanup candidates");
    } finally {
      setCleanupLoading(false);
    }
  };

  const toggleCleanup = (hash) => {
    const s = new Set(selectedCleanup);
    s.has(hash) ? s.delete(hash) : s.add(hash);
    setSelectedCleanup(s);
  };

  const allCleanupSelected = (cleanupCandidates?.length || 0) > 0 && cleanupCandidates.every((c) => selectedCleanup.has(c.hash));
  const toggleSelectAllCleanup = () => {
    setSelectedCleanup(allCleanupSelected ? new Set() : new Set((cleanupCandidates || []).map((c) => c.hash)));
  };

  const runCleanup = async () => {
    if (selectedCleanup.size === 0) return;
    if (!window.confirm(
      `Remove ${selectedCleanup.size} torrent(s) from qBittorrent and delete their downloaded files? The library's copy is a separate hardlink and is never touched — only the download-folder copy goes away.`
    )) return;
    setCleaningUp(true);
    try {
      const { data } = await api.post("/qbit/cleanup", { hashes: [...selectedCleanup] });
      toast.success(`Cleaned up ${data.removed} torrent(s)`);
      loadCleanupCandidates();
      loadTorrents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaningUp(false);
    }
  };

  // ---- Maintain Everything (one-click, background job) ----
  const [maintainRunning, setMaintainRunning] = useState(false);
  const [maintainStage, setMaintainStage] = useState("");
  const [maintainSummary, setMaintainSummary] = useState(null);
  const [stats, setStats] = useState(null);
  const [fixingPending, setFixingPending] = useState(false);

  const loadStats = async (refresh = false) => {
    try {
      const { data } = await api.get(`/library/stats${refresh ? "?refresh=true" : ""}`);
      setStats(data);
    } catch { /* non-fatal: header just stays blank */ }
  };
  const maintainPoll = useRef(null);

  const checkMaintainStatus = async () => {
    const { data } = await api.get("/library/maintain/status");
    setMaintainRunning(data.running);
    setMaintainStage(data.stage || "");
    if (data.error) toast.error(`Maintenance failed: ${data.error}`);
    if (data.summary) setMaintainSummary(data.summary);
    return data.running;
  };

  const startMaintainPolling = () => {
    if (maintainPoll.current) return;
    maintainPoll.current = setInterval(async () => {
      const stillRunning = await checkMaintainStatus();
      if (!stillRunning) {
        clearInterval(maintainPoll.current);
        maintainPoll.current = null;
        loadScan(); // refresh per-kind view
        loadStats(); // the maintain job already recomputed + cached the header stats — no forced du
        checkCleanupStatus(); // the backend job already ran the deep scan — just pick up its findings, don't rescan
      }
    }, 2000);
  };

  useEffect(() => {
    loadStats();
    checkMaintainStatus().then((isRunning) => { if (isRunning) startMaintainPolling(); });
    return () => { if (maintainPoll.current) clearInterval(maintainPoll.current); };
  }, []);

  const maintainEverything = async () => {
    if (!window.confirm(
      "Maintain Library: import completed Radarr/Sonarr downloads, clear finished torrents, import new files, " +
      "remove entries whose file is gone, and fix orphaned records, stuck jobs, empty folders and duplicate imports " +
      "— across movies, TV and music. File-deleting duplicate resolutions (keep best, delete the rest) are reported " +
      "for you to confirm afterward, not auto-run. Runs in the background. Continue?"
    )) return;
    try {
      await api.post("/library/maintain/start");
      setMaintainRunning(true);
      setMaintainSummary(null);
      startMaintainPolling();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start maintenance");
    }
  };

  const fixPendingDuplicates = async () => {
    const items = maintainSummary?.pending?.duplicate_content || [];
    if (!items.length) return;
    if (!window.confirm(
      `Delete ${items.length} redundant duplicate file${items.length === 1 ? "" : "s"} from the NAS? ` +
      "The best (largest/highest-quality) copy of each is kept; the database files it points at are never deleted."
    )) return;
    setFixingPending(true);
    try {
      await api.post("/library/cleanup/fix", { category: "duplicate_content", items });
      toast.success(`Deleted ${items.length} duplicate file${items.length === 1 ? "" : "s"}`);
      setMaintainSummary((p) => (p ? { ...p, pending: { ...p.pending, duplicate_content: [] } } : p));
      loadStats(true);
      checkCleanupStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete duplicates");
    } finally {
      setFixingPending(false);
    }
  };

  // ---- Per-kind scan (new on disk / missing from disk) ----
  // Music defaults to the active tab since it's the only kind without an auto-import
  // path (Radarr/Sonarr webhooks cover movies/TV) — those two stay collapsed by default
  // so their long on-disk listing doesn't load/scroll past on every visit.
  const [kind, setKind] = useState("music");
  const [results, setResults] = useState([]);
  const [resultsExpanded, setResultsExpanded] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [enrich, setEnrich] = useState(null);
  const [missing, setMissing] = useState([]);
  const [selectedMissing, setSelectedMissing] = useState(new Set());
  const [missingLoading, setMissingLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const requestSeq = useRef(0);

  const loadScan = async (k = kind) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setMissingLoading(true);
    setSelected(new Set());
    setSelectedMissing(new Set());
    try {
      const { data } = await api.get(`/scan/${k}`);
      if (seq !== requestSeq.current) return; // a newer tab switch/rescan superseded this response
      setResults(data);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
    try {
      const { data } = await api.get(`/scan/missing/${k}`);
      if (seq !== requestSeq.current) return;
      setMissing(data);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      toast.error(err.response?.data?.detail || "Missing-file check failed");
    } finally {
      if (seq === requestSeq.current) setMissingLoading(false);
    }
  };

  useEffect(() => {
    setResults([]);
    setMissing([]);
    setResultsExpanded(kind === "music");
    loadScan(kind);
  }, [kind]);

  useEffect(() => {
    if (!enrich?.running) return;
    const t = setInterval(async () => {
      const { data } = await api.get("/music/enrich/status");
      setEnrich(data);
      if (!data.running) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
  }, [enrich?.running]);

  const toggle = (path) => {
    const s = new Set(selected);
    s.has(path) ? s.delete(path) : s.add(path);
    setSelected(s);
  };

  const importable = results.filter((r) => !r.already_imported);
  const allSelected = importable.length > 0 && importable.every((r) => selected.has(r.storage_path));

  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(importable.map((r) => r.storage_path)));
  };

  const importSelected = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    try {
      const items = results.filter((r) => selected.has(r.storage_path));
      const { data } = await api.post("/scan/import", { kind, items });
      toast.success(`Imported ${data.imported} item(s)`);
      loadScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const toggleMissing = (id) => {
    const s = new Set(selectedMissing);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedMissing(s);
  };

  const allMissingSelected = missing.length > 0 && missing.every((r) => selectedMissing.has(r.id));

  const toggleSelectAllMissing = () => {
    setSelectedMissing(allMissingSelected ? new Set() : new Set(missing.map((r) => r.id)));
  };

  const removeSelected = async () => {
    if (selectedMissing.size === 0) return;
    if (!window.confirm(`Remove ${selectedMissing.size} item(s) from the library? Their files are no longer on disk — this only deletes the database entries.`)) return;
    setRemoving(true);
    try {
      const items = missing.filter((r) => selectedMissing.has(r.id));
      const { data } = await api.post("/scan/remove", { kind, items });
      toast.success(`Removed ${data.removed} item(s)`);
      loadScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    } finally {
      setRemoving(false);
    }
  };

  const syncNow = async () => {
    if (!window.confirm(
      `Import every new ${kind === "tv" ? "episode" : kind === "music" ? "song" : "movie"} found on disk, and remove every library entry whose file is gone? Removal only deletes the database entry, never a file on disk.`
    )) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/scan/sync/${kind}`);
      toast.success(`Synced — imported ${data.imported}, removed ${data.removed}`);
      loadScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const startEnrich = async () => {
    try {
      await api.post("/music/enrich");
      const { data } = await api.get("/music/enrich/status");
      setEnrich(data);
      toast.success("Fetching metadata & covers from MusicBrainz…");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start enrichment");
    }
  };

  const label = (r) => {
    if (kind === "movies") return `${r.title}${r.year ? ` (${r.year})` : ""}`;
    if (kind === "tv") return `${r.show_title} · S${r.season_number}E${r.episode_number ?? "?"} · ${r.title}`;
    return `${r.artist} — ${r.album} — ${r.title}`;
  };

  // ---- Deep cleanup scan (orphaned records, stuck jobs, duplicates) ----
  const [cleanupFindings, setCleanupFindings] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [fixing, setFixing] = useState({});
  const cleanupPoll = useRef(null);

  const checkCleanupStatus = async () => {
    const { data } = await api.get("/library/cleanup/scan/status");
    setCleanupRunning(data.running);
    if (data.error) toast.error(`Deep scan failed: ${data.error}`);
    if (data.findings) setCleanupFindings(data.findings);
    return data;
  };

  const startCleanupPolling = () => {
    if (cleanupPoll.current) return;
    cleanupPoll.current = setInterval(async () => {
      const { running } = await checkCleanupStatus();
      if (!running) {
        clearInterval(cleanupPoll.current);
        cleanupPoll.current = null;
      }
    }, 2000);
  };

  useEffect(() => {
    // Show the LAST scan's findings if the backend still has them; never auto-launch a fresh deep
    // scan on mount — it's a minutes-long full-NAS sweep, so it stays an explicit "Rescan" click.
    checkCleanupStatus().then((d) => { if (d.running) startCleanupPolling(); });
    return () => { if (cleanupPoll.current) clearInterval(cleanupPoll.current); };
  }, []);

  const runCleanupScan = async () => {
    try {
      await api.post("/library/cleanup/scan/start");
      setCleanupRunning(true);
      setCleanupFindings(null);
      startCleanupPolling();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start deep scan");
    }
  };

  const scanCleanup = async () => {
    if (!window.confirm(
      "This checks every movie, episode, and track's file against the NAS, which can take a while on a large library (roughly a minute or more) since each check is a network round trip. It runs in the background and won't block the rest of the app — you can navigate away or stop it anytime. Continue?"
    )) return;
    runCleanupScan();
  };

  const stopCleanup = async () => {
    try {
      await api.post("/library/cleanup/scan/stop");
      setCleanupRunning(false);
      if (cleanupPoll.current) { clearInterval(cleanupPoll.current); cleanupPoll.current = null; }
      toast.success("Deep scan stopped");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not stop deep scan");
    }
  };

  const fixCleanup = async (category, items) => {
    if (items.length === 0) return;
    if (category === "duplicate_content" && !window.confirm(
      `Delete ${items.length} duplicate file(s) from the NAS and remove their library entries? This permanently deletes the actual redundant file, not just the database record — the copy being kept is untouched.`
    )) return;
    setFixing((p) => ({ ...p, [category]: true }));
    try {
      const { data } = await api.post("/library/cleanup/fix", { category, items });
      toast.success(`Fixed ${data.fixed} item(s)`);
      runCleanupScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fix failed");
    } finally {
      setFixing((p) => ({ ...p, [category]: false }));
    }
  };

  const deepTotalIssues = cleanupFindings
    ? DEEP_CATEGORIES.reduce((n, c) => n + (cleanupFindings[c.key]?.length || 0), 0)
    : 0;

  const sumVals = (o) => Object.values(o || {}).reduce((a, b) => a + (b || 0), 0);
  const rundownTotal = cleanupFindings
    ? RUNDOWN_CATEGORIES.reduce((n, c) => n + (cleanupFindings[c.key]?.length || 0), 0)
    : 0;
  const arrImported = maintainSummary ? ((maintainSummary.arr?.movies || 0) + (maintainSummary.arr?.episodes || 0)) : 0;
  const pendingDupes = maintainSummary?.pending?.duplicate_content || [];
  const reclaimedBytes = (() => {
    const sp = maintainSummary?.space;
    if (!sp) return null;
    return ["video", "music"].reduce((r, k) => {
      const a = sp.after?.[k], b = sp.before?.[k];
      return r + ((typeof a === "number" && typeof b === "number") ? a - b : 0);
    }, 0);
  })();

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24" data-testid="library-page">
      <div className="px-6 md:px-12 max-w-[1500px] mx-auto">
        <span className="text-xs uppercase tracking-[0.3em] text-[#D9381E]">Filesystem &amp; Maintenance</span>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-white mt-3">Library</h1>
        <p className="text-[#8A8A8A] mt-4 max-w-2xl">
          Everything that keeps the library in sync with the NAS in one place: import new files, remove entries whose
          files are gone, and clean up orphaned records, stuck jobs, and duplicates — for movies, TV, and music.
          Radarr/Sonarr's own imports are untouched.
        </p>

        {/* ---- Storage rundown ---- */}
        {stats && (
          <>
            <div className="mt-8 grid gap-4 md:grid-cols-3" data-testid="library-stats">
              {[
                { k: "movies", label: "Movies", count: stats.counts?.movies, size: stats.sizes?.movies },
                { k: "tv", label: "TV Shows", count: stats.counts?.shows, sub: `${(stats.counts?.episodes || 0).toLocaleString()} episodes`, size: stats.sizes?.tv },
                { k: "music", label: "Music", count: stats.counts?.tracks, size: stats.sizes?.music },
              ].map((c) => (
                <div key={c.k} className="border border-[#222] bg-[#0b0b0b] p-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A]">{c.label}</div>
                  <div className="text-3xl font-black text-white mt-1">{(c.count ?? 0).toLocaleString()}</div>
                  <div className="text-sm text-[#8A8A8A] mt-1">{c.sub ? `${c.sub} · ` : ""}{formatBytes(c.size)}</div>
                </div>
              ))}
            </div>
            {stats.capacity && (
              <div className="mt-4 space-y-3">
                {[["video", "Video NAS"], ["music", "Music NAS"]].map(([k, label]) => {
                  const cap = stats.capacity[k];
                  if (!cap || !cap.total) return null;
                  const pct = Math.min(100, Math.round((cap.used / cap.total) * 100));
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-xs text-[#8A8A8A] mb-1">
                        <span>{label}</span>
                        <span>{formatBytes(cap.used)} used · {formatBytes(cap.free)} free · {formatBytes(cap.total)} total ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-[#1a1a1a] rounded overflow-hidden">
                        <div className={`h-full ${pct > 90 ? "bg-[#D9381E]" : "bg-[#86efac]"}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ---- Findings rundown (auto-scanned on load, before any button) ---- */}
        <div className="mt-8 border border-[#222] bg-[#0b0b0b] p-6" data-testid="findings-rundown">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} strokeWidth={1.5} className="text-[#D9381E]" />
              <h2 className="text-white text-xl font-semibold">Findings</h2>
              {cleanupRunning
                ? <span className="text-xs text-[#fcd34d] animate-pulse">scanning the library…</span>
                : cleanupFindings && <span className={`text-xs ${rundownTotal ? "text-[#fcd34d]" : "text-[#86efac]"}`}>
                    {rundownTotal ? `${rundownTotal} issue${rundownTotal === 1 ? "" : "s"} found` : "all clean"}</span>}
            </div>
            <button onClick={runCleanupScan} disabled={cleanupRunning}
              className="flex items-center gap-2 border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50"
              data-testid="findings-rescan">
              <RefreshCcw size={14} strokeWidth={1.5} className={cleanupRunning ? "animate-spin" : ""} /> Rescan
            </button>
          </div>
          {cleanupFindings ? (
            <div className="mt-4 grid gap-x-8 gap-y-1 md:grid-cols-2">
              {RUNDOWN_CATEGORIES.map((c) => {
                const n = cleanupFindings[c.key]?.length || 0;
                return (
                  <div key={c.key} className="flex items-center justify-between border-b border-[#1a1a1a] py-2 text-sm">
                    <span className="text-[#C9C9C9]">{c.label}{c.destructive && n > 0 && <span className="text-[#8A8A8A]"> · needs confirm</span>}</span>
                    <span className={n ? "text-[#fcd34d] font-semibold" : "text-[#86efac]"}>{n || "✓"}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-[#8A8A8A] mt-3">{cleanupRunning ? "Scanning the NAS and database…" : "No scan yet — hit Rescan."}</p>
          )}
          <p className="text-xs text-[#8A8A8A] mt-3">
            A read-only snapshot of what needs attention, refreshed each visit. &ldquo;Maintain Library&rdquo; below fixes all of it
            in one pass (file deletions are confirmed separately).
          </p>
        </div>

        {/* ---- Maintain Everything ---- */}
        <div className="mt-8 border border-[#D9381E]/40 bg-[#D9381E]/5 p-6">
          <div className="flex items-center gap-4 flex-wrap">
            <button onClick={maintainEverything} disabled={maintainRunning}
              className="flex items-center gap-2 bg-[#D9381E] hover:bg-[#ED4B32] disabled:opacity-50 text-white px-6 py-3 text-xs uppercase tracking-[0.2em]"
              data-testid="maintain-everything-btn">
              <Zap size={16} strokeWidth={1.5} className={maintainRunning ? "animate-pulse" : ""} />
              {maintainRunning ? "Maintaining…" : "Maintain Library"}
            </button>
            {maintainRunning && <span className="text-xs text-[#fcd34d] animate-pulse" data-testid="maintain-stage">{maintainStage || "Working…"}</span>}
            {!maintainRunning && maintainSummary && (
              <span className="text-sm text-[#86efac]" data-testid="maintain-summary">
                Imported {sumVals(maintainSummary.imported) + arrImported}, removed {sumVals(maintainSummary.removed)}, fixed {sumVals(maintainSummary.fixed)}
                {maintainSummary.downloads_removed ? `, ${maintainSummary.downloads_removed} downloads cleared` : ""}
                {reclaimedBytes > 0 ? `, ${formatBytes(reclaimedBytes)} reclaimed` : ""}.
              </span>
            )}
          </div>
          <p className="text-xs text-[#8A8A8A] mt-3">
            One click does it all: imports completed Radarr/Sonarr downloads, clears finished torrents (deletes the
            download originals), imports new files, removes entries whose file is gone, and fixes orphaned records,
            stuck jobs, empty folders and duplicate imports — across Movies, TV and Music. File-deleting duplicate
            resolutions (keep best, delete the rest) are reported below for a single confirm — never auto-run. The
            sections further down stay available for reviewing or fixing any one thing by hand.
          </p>
          {!maintainRunning && pendingDupes.length > 0 && (
            <div className="mt-4 border border-[#fcd34d]/40 bg-[#fcd34d]/5 p-4" data-testid="maintain-pending">
              <div className="flex items-center gap-2 text-[#fcd34d] text-sm font-semibold">
                <AlertTriangle size={16} strokeWidth={1.5} /> {pendingDupes.length} duplicate file{pendingDupes.length === 1 ? "" : "s"} to review
              </div>
              <p className="text-xs text-[#8A8A8A] mt-1">
                The best copy of each title is kept; these redundant files get deleted from the NAS. The library
                entries (database files) they point at are never touched.
              </p>
              <ul className="text-xs text-[#8A8A8A] mt-2 max-h-40 overflow-auto list-disc pl-5 space-y-0.5">
                {pendingDupes.slice(0, 50).map((d, i) => (
                  <li key={i}>{d.label} <span className="opacity-60">— {d.detail}</span></li>
                ))}
                {pendingDupes.length > 50 && <li className="opacity-60">…and {pendingDupes.length - 50} more</li>}
              </ul>
              <button onClick={fixPendingDuplicates} disabled={fixingPending}
                className="mt-3 flex items-center gap-2 bg-[#7f1d1d] hover:bg-[#991b1b] disabled:opacity-50 text-white px-5 py-2 text-xs uppercase tracking-[0.2em]"
                data-testid="maintain-pending-fix">
                <Trash2 size={14} strokeWidth={1.5} /> {fixingPending ? "Deleting…" : `Delete ${pendingDupes.length} redundant file${pendingDupes.length === 1 ? "" : "s"}`}
              </button>
            </div>
          )}
        </div>

        {/* ---- Downloads (qBittorrent) ---- */}
        <div className="mt-12 flex items-center gap-2">
          <HardDrive size={16} strokeWidth={1.5} className="text-[#D9381E]" />
          <h2 className="text-white text-xl font-semibold">Downloads</h2>
          {torrents && <span className="text-[#8A8A8A] text-sm">({torrents.length} torrent{torrents.length === 1 ? "" : "s"}, live)</span>}
        </div>

        {torrentsError ? (
          <p className="text-[#fca5a5] mt-3 text-sm">{torrentsError}. Configure qBittorrent under Settings.</p>
        ) : (
          <>
            <div className="mt-4 flex gap-3 flex-wrap items-center">
              <button onClick={pauseAllDownloads} disabled={pausingAll || !torrents?.length}
                className="flex items-center gap-2 border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50"
                data-testid="qbit-pause-all-btn">
                <Pause size={14} strokeWidth={1.5} /> {pausingAll ? "Pausing…" : "Pause All"}
              </button>
              <button onClick={resumeAllDownloads} disabled={resumingAll || !torrents?.length}
                className="flex items-center gap-2 border border-[#86efac]/40 hover:border-[#86efac] text-[#86efac] px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50"
                data-testid="qbit-resume-all-btn">
                <Play size={14} strokeWidth={1.5} /> {resumingAll ? "Resuming…" : "Resume All"}
              </button>
            </div>

            <div className="mt-4 border border-[#222]">
              {(torrents || []).map((t) => (
                <div key={t.hash} className="flex items-center gap-4 px-5 py-3 border-b border-[#222] last:border-b-0" data-testid={`qbit-torrent-${t.hash}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm truncate">{t.name}</p>
                    <div className="mt-2 h-1 bg-[#222] rounded-full overflow-hidden">
                      <div className="h-full bg-[#D9381E]" style={{ width: `${t.progress}%` }} />
                    </div>
                  </div>
                  <div className="shrink-0 text-right w-28">
                    <p className="text-xs text-[#8A8A8A]">{TORRENT_STATE_LABELS[t.state] || t.state}</p>
                    <p className="text-xs text-[#666]">{t.progress}%</p>
                  </div>
                  <div className="shrink-0 text-right w-32 text-xs text-[#8A8A8A]">
                    <p>↓ {formatBytes(t.dlspeed)}/s</p>
                    <p>↑ {formatBytes(t.upspeed)}/s</p>
                  </div>
                  <div className="shrink-0 text-right w-20 text-xs text-[#666]">
                    <p>{formatBytes(t.size)}</p>
                    <p>ETA {formatEta(t.eta)}</p>
                  </div>
                </div>
              ))}
              {torrents && torrents.length === 0 && (
                <div className="px-5 py-8 text-center text-[#8A8A8A] text-sm">No torrents in qBittorrent right now.</div>
              )}
              {torrents === null && (
                <div className="px-5 py-8 text-center text-[#8A8A8A] text-sm">Loading…</div>
              )}
            </div>

            {/* ---- Cleanup: completed downloads already in the library ---- */}
            <div className="mt-8 flex items-center gap-2">
              <Trash2 size={15} strokeWidth={1.5} className="text-[#D9381E]" />
              <h3 className="text-white text-base font-semibold">Completed &amp; Already in Library</h3>
            </div>
            <p className="text-[#8A8A8A] mt-2 text-sm max-w-2xl">
              Finished torrents whose file is already imported into the library (matched by the actual file on disk,
              not just the name — safe even though Radarr/Sonarr renames things on import). Removing these deletes
              only the download-folder copy; the library's copy is a separate hardlink and is untouched.
            </p>
            <div className="mt-4 flex gap-3 flex-wrap items-center">
              <button onClick={loadCleanupCandidates} disabled={cleanupLoading} className="flex items-center gap-2 border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50" data-testid="qbit-cleanup-check-btn">
                <RefreshCcw size={14} strokeWidth={1.5} className={cleanupLoading ? "animate-spin" : ""} /> {cleanupLoading ? "Checking…" : "Check Again"}
              </button>
              <button onClick={toggleSelectAllCleanup} disabled={!cleanupCandidates?.length} className="flex items-center gap-2 border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50" data-testid="qbit-cleanup-select-all">
                {allCleanupSelected ? <CheckSquare size={14} strokeWidth={1.5} /> : <Square size={14} strokeWidth={1.5} />} {allCleanupSelected ? "Deselect All" : "Select All"}
              </button>
              <button onClick={runCleanup} disabled={selectedCleanup.size === 0 || cleaningUp} className="flex items-center gap-2 bg-[#7f1d1d] hover:bg-[#991b1b] disabled:opacity-50 text-white px-5 py-2 text-xs uppercase tracking-[0.2em]" data-testid="qbit-cleanup-run-btn">
                <Trash2 size={14} strokeWidth={1.5} /> {cleaningUp ? "Cleaning…" : `Clean Up ${selectedCleanup.size > 0 ? `(${selectedCleanup.size})` : ""}`}
              </button>
            </div>

            <div className="mt-4 border border-[#7f1d1d]/40">
              {(cleanupCandidates || []).map((c) => (
                <button key={c.hash} onClick={() => toggleCleanup(c.hash)}
                  className={`w-full flex items-center gap-4 px-5 py-3 border-b border-[#7f1d1d]/40 last:border-b-0 text-left transition-colors ${
                    selectedCleanup.has(c.hash) ? "bg-[#7f1d1d]/20" : "hover:bg-[#0F0F0F]"
                  }`}
                  data-testid={`qbit-cleanup-row-${c.hash}`}>
                  <div className={`w-5 h-5 shrink-0 border flex items-center justify-center ${selectedCleanup.has(c.hash) ? "bg-[#7f1d1d] border-[#7f1d1d]" : "border-[#222]"}`}>
                    {selectedCleanup.has(c.hash) && <Check size={12} strokeWidth={2} color="white" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm truncate">{c.name}</p>
                    <p className="text-xs text-[#666] truncate">→ {c.matched_library_path}</p>
                  </div>
                  <span className="text-xs text-[#8A8A8A] shrink-0">{formatBytes(c.size)}</span>
                </button>
              ))}
              {cleanupCandidates && cleanupCandidates.length === 0 && (
                <div className="px-5 py-8 text-center text-[#8A8A8A] text-sm">Nothing to clean up right now.</div>
              )}
              {cleanupCandidates === null && (
                <div className="px-5 py-8 text-center text-[#8A8A8A] text-sm">Checking…</div>
              )}
            </div>
          </>
        )}

        {/* ---- Manual tools (collapsed) — all covered by "Maintain Library" above ---- */}
        <details className="mt-12 border border-[#222] rounded">
          <summary className="cursor-pointer select-none px-5 py-3 text-xs uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white">
            Manual tools — per-tab import, missing files &amp; deep scan (optional; Maintain Library above already does all of this)
          </summary>
          <div className="px-5 pb-6">
        {/* ---- Per-kind scan ---- */}
        <div className="mt-6 flex gap-3 flex-wrap">
          {KINDS.map((k) => (
            <button key={k.key} onClick={() => setKind(k.key)}
              className={`text-sm px-4 py-2 border transition-colors ${kind === k.key ? "border-[#D9381E] text-white bg-[#D9381E]/10" : "border-[#222] text-[#8A8A8A] hover:text-white"}`}
              data-testid={`scan-tab-${k.key}`}>
              {k.label}
            </button>
          ))}
        </div>

        <div className="mt-6 flex gap-3 flex-wrap items-center">
          <button onClick={() => loadScan()} disabled={loading} className="flex items-center gap-2 border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50" data-testid="scan-refresh">
            <RefreshCcw size={14} strokeWidth={1.5} className={loading ? "animate-spin" : ""} /> Rescan
          </button>
          <button onClick={syncNow} disabled={syncing} title="Import every new file and remove every missing entry in one step" className="flex items-center gap-2 border border-[#86efac]/40 hover:border-[#86efac] text-[#86efac] px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50" data-testid="scan-sync-btn">
            <RefreshCcw size={14} strokeWidth={1.5} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync This Tab"}
          </button>
          <button onClick={toggleSelectAll} disabled={importable.length === 0} className="flex items-center gap-2 border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50" data-testid="scan-select-all">
            {allSelected ? <CheckSquare size={14} strokeWidth={1.5} /> : <Square size={14} strokeWidth={1.5} />} {allSelected ? "Deselect All" : "Select All"}
          </button>
          <button onClick={importSelected} disabled={selected.size === 0 || importing} className="flex items-center gap-2 bg-[#D9381E] hover:bg-[#ED4B32] disabled:opacity-50 text-white px-5 py-2 text-xs uppercase tracking-[0.2em]" data-testid="scan-import-btn">
            <Download size={14} strokeWidth={1.5} /> Import {selected.size > 0 ? `(${selected.size})` : ""}
          </button>
          {kind === "music" && (
            <button onClick={startEnrich} disabled={enrich?.running} className="flex items-center gap-2 border border-white/10 bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white px-5 py-2 text-xs uppercase tracking-[0.2em]" data-testid="scan-enrich-btn">
              <Sparkles size={14} strokeWidth={1.5} /> {enrich?.running ? `Fetching covers… ${enrich.done}/${enrich.total}` : "Fetch metadata & covers"}
            </button>
          )}
        </div>
        <p className="text-xs text-[#8A8A8A] mt-2">
          Rescan re-checks disk without changing anything · Sync This Tab imports every new file and removes every
          missing entry for this tab in one step · Select All + Import lets you cherry-pick which new files to add.
        </p>

        <div className="mt-6 flex items-center gap-3">
          <p className="text-[#8A8A8A] text-sm">
            {loading ? "Scanning…" : `${importable.length} new of ${results.length} found on disk.`}
          </p>
          {!loading && results.length > 0 && (
            <button onClick={() => setResultsExpanded((e) => !e)}
              className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white transition-colors"
              data-testid="scan-results-toggle">
              {resultsExpanded ? "Hide list" : "Show list"}
            </button>
          )}
        </div>

        {resultsExpanded && (
          <div className="mt-6 border border-[#222]">
            {results.map((r) => (
              <button key={r.storage_path} onClick={() => !r.already_imported && toggle(r.storage_path)}
                disabled={r.already_imported}
                className={`w-full flex items-center gap-4 px-5 py-3 border-b border-[#222] last:border-b-0 text-left transition-colors ${
                  r.already_imported ? "opacity-40 cursor-not-allowed" : selected.has(r.storage_path) ? "bg-[#D9381E]/10" : "hover:bg-[#0F0F0F]"
                }`}
                data-testid={`scan-row-${r.storage_path}`}>
                <div className={`w-5 h-5 shrink-0 border flex items-center justify-center ${selected.has(r.storage_path) ? "bg-[#D9381E] border-[#D9381E]" : "border-[#222]"}`}>
                  {selected.has(r.storage_path) && <Check size={12} strokeWidth={2} color="white" />}
                </div>
                <span className="text-white text-sm truncate flex-1">{label(r)}</span>
                {r.already_imported && <span className="text-[10px] uppercase tracking-[0.2em] text-[#86efac] shrink-0">Imported</span>}
              </button>
            ))}
            {results.length === 0 && !loading && (
              <div className="px-5 py-8 text-center text-[#8A8A8A] text-sm">Nothing found on disk for this category.</div>
            )}
          </div>
        )}

        <div className="mt-16 flex items-center gap-2">
          <AlertTriangle size={16} strokeWidth={1.5} className="text-[#D9381E]" />
          <h2 className="text-white text-xl font-semibold">Missing from disk</h2>
        </div>
        <p className="text-[#8A8A8A] mt-2 text-sm max-w-2xl">
          These are in the library but their file no longer exists on the NAS — deleted, moved, or renamed outside StreamHoard.
          Removing them only deletes the database entry; it never touches anything on disk.
        </p>

        <div className="mt-4 flex gap-3 flex-wrap items-center">
          <button onClick={toggleSelectAllMissing} disabled={missing.length === 0} className="flex items-center gap-2 border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50" data-testid="scan-select-all-missing">
            {allMissingSelected ? <CheckSquare size={14} strokeWidth={1.5} /> : <Square size={14} strokeWidth={1.5} />} {allMissingSelected ? "Deselect All" : "Select All"}
          </button>
          <button onClick={removeSelected} disabled={selectedMissing.size === 0 || removing} className="flex items-center gap-2 bg-[#7f1d1d] hover:bg-[#991b1b] disabled:opacity-50 text-white px-5 py-2 text-xs uppercase tracking-[0.2em]" data-testid="scan-remove-btn">
            <Trash2 size={14} strokeWidth={1.5} /> Remove {selectedMissing.size > 0 ? `(${selectedMissing.size})` : ""}
          </button>
        </div>

        <p className="text-[#8A8A8A] mt-4 text-sm">
          {missingLoading ? "Checking…" : `${missing.length} missing.`}
        </p>

        <div className="mt-4 border border-[#222]">
          {missing.map((r) => (
            <button key={r.id} onClick={() => toggleMissing(r.id)}
              className={`w-full flex items-center gap-4 px-5 py-3 border-b border-[#222] last:border-b-0 text-left transition-colors ${
                selectedMissing.has(r.id) ? "bg-[#7f1d1d]/20" : "hover:bg-[#0F0F0F]"
              }`}
              data-testid={`scan-missing-row-${r.id}`}>
              <div className={`w-5 h-5 shrink-0 border flex items-center justify-center ${selectedMissing.has(r.id) ? "bg-[#7f1d1d] border-[#7f1d1d]" : "border-[#222]"}`}>
                {selectedMissing.has(r.id) && <Check size={12} strokeWidth={2} color="white" />}
              </div>
              <span className="text-white text-sm truncate flex-1">{r.label}</span>
              <span className="text-[#8A8A8A] text-xs truncate max-w-[30%] shrink-0">{r.storage_path}</span>
            </button>
          ))}
          {missing.length === 0 && !missingLoading && (
            <div className="px-5 py-8 text-center text-[#8A8A8A] text-sm">Nothing missing — every entry in the library still has its file.</div>
          )}
        </div>

        {/* ---- Deep cleanup scan ---- */}
        <div className="mt-16 flex items-center gap-2">
          <Wrench size={16} strokeWidth={1.5} className="text-[#D9381E]" />
          <h2 className="text-white text-xl font-semibold">Deep Scan — orphaned records, stuck jobs, duplicates</h2>
        </div>
        <p className="text-[#8A8A8A] mt-2 text-sm max-w-2xl">
          A slower whole-library pass across movies, shows, and music for issues the per-tab scans above don't catch.
          Nothing is deleted until you click Fix.
        </p>

        <div className="mt-4 flex items-center gap-4">
          {cleanupRunning ? (
            <button onClick={stopCleanup} className="flex items-center gap-2 border border-[#D9381E] text-[#D9381E] hover:bg-[#D9381E]/10 px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors" data-testid="cleanup-stop">
              <Square size={12} strokeWidth={1.5} fill="currentColor" /> Stop Scan
            </button>
          ) : (
            <button onClick={scanCleanup} className="flex items-center gap-2 border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors" data-testid="cleanup-scan">
              <RefreshCcw size={14} strokeWidth={1.5} /> {cleanupFindings ? "Rescan" : "Run Deep Scan"}
            </button>
          )}
          {cleanupRunning && <span className="text-xs text-[#fcd34d] animate-pulse" data-testid="cleanup-running">Scanning… this can take a minute</span>}
          {!cleanupRunning && cleanupFindings && (
            <span className="flex items-center gap-2 text-sm" data-testid="cleanup-summary">
              {deepTotalIssues === 0 ? (
                <><ShieldCheck size={16} className="text-[#86efac]" /> <span className="text-[#86efac]">No issues found</span></>
              ) : (
                <span className="text-[#fcd34d]">{deepTotalIssues} issue(s) found</span>
              )}
            </span>
          )}
        </div>

        <div className="mt-8 space-y-8 mb-24">
          {DEEP_CATEGORIES.map((cat) => {
            const items = cleanupFindings?.[cat.key] || [];
            if (cleanupFindings && items.length === 0) return null;
            return (
              <div key={cat.key} className={`border ${cat.destructive ? "border-[#7f1d1d]" : "border-[#222]"}`} data-testid={`cleanup-section-${cat.key}`}>
                <div className={`flex items-center justify-between px-5 py-4 border-b ${cat.destructive ? "border-[#7f1d1d] bg-[#7f1d1d]/10" : "border-[#222] bg-[#0F0F0F]"}`}>
                  <div>
                    <h3 className="text-white text-lg font-display">{cat.label} <span className="text-[#8A8A8A] text-sm">({items.length})</span></h3>
                    <p className="text-xs text-[#8A8A8A] mt-1">{cat.desc}</p>
                  </div>
                  <button onClick={() => fixCleanup(cat.key, items)} disabled={fixing[cat.key] || items.length === 0}
                    className={`flex items-center gap-2 disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-[0.2em] shrink-0 ${cat.destructive ? "bg-[#7f1d1d] hover:bg-[#991b1b]" : "bg-[#D9381E] hover:bg-[#ED4B32]"}`}
                    data-testid={`cleanup-fix-all-${cat.key}`}>
                    <Wrench size={13} strokeWidth={1.5} /> {cat.action} All
                  </button>
                </div>
                <div>
                  {items.map((it) => (
                    <div key={`${it.content_type}-${it.id}`} className="flex items-center justify-between gap-4 px-5 py-3 border-b border-[#222] last:border-b-0" data-testid={`cleanup-item-${it.id}`}>
                      <div className="min-w-0">
                        <p className="text-white text-sm truncate">{it.label}</p>
                        <p className="text-xs text-[#666] truncate">{it.detail}</p>
                      </div>
                      <button onClick={() => fixCleanup(cat.key, [it])} disabled={fixing[cat.key]}
                        className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#fca5a5] hover:text-[#fca5a5] text-[#8A8A8A] px-3 py-2 shrink-0 disabled:opacity-50"
                        data-testid={`cleanup-fix-${it.id}`}>
                        <Trash2 size={12} strokeWidth={1.5} /> {cat.action}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {!cleanupRunning && cleanupFindings && deepTotalIssues === 0 && (
            <div className="text-center py-16 text-[#8A8A8A]">
              <ShieldCheck size={40} strokeWidth={1.5} className="mx-auto mb-4 text-[#86efac]" />
              Nothing found — no orphaned records, stuck jobs, or duplicates.
            </div>
          )}
          {!cleanupRunning && !cleanupFindings && (
            <div className="text-center py-16 text-[#8A8A8A]">
              Click "Run Deep Scan" above to check for issues, or use "Maintain Everything" at the top to do it all at once.
            </div>
          )}
        </div>
          </div>
        </details>
      </div>
    </div>
  );
}
